<template>
  <main class="physical-card-open-history-detail-page">
    <AppNavBar
      :title="t('card.openCardHistoryPhysicalDetailTitle')"
      background-color="#ffffff"
      fallback-to="/wallet/open-card/history"
    />

    <template v-if="status === 'approved'">
      <section
        class="physical-card-detail-hero physical-card-detail-hero--approved"
      >
        <div class="physical-card-detail-success-image">
          <app-image :src="physicalSuccessImage" alt="" />
        </div>
        <div class="physical-card-detail-success-content">
          <h1>{{ t("card.openCardHistoryPhysicalActivatedTitle") }}</h1>
          <span class="physical-card-detail-status is-approved">
            {{ t("card.openCardHistoryPhysicalActivationStatus") }}
          </span>
          <p>{{ t("card.openCardHistoryPhysicalActivatedDesc") }}</p>
        </div>
      </section>

      <section class="physical-card-detail-info">
        <h2>{{ t("card.openCardHistoryInfo") }}</h2>
        <div class="physical-card-detail-info__list">
          <div class="physical-card-detail-info__row">
            <span>{{ t("card.openCardHistoryCardName") }}</span>
            <strong>{{ cardName }}</strong>
          </div>
          <div class="physical-card-detail-info__row">
            <span>{{ t("card.openCardHistoryAppliedAt") }}</span>
            <strong>{{ appliedAt }}</strong>
          </div>
          <div class="physical-card-detail-info__row">
            <span>{{ t("card.openCardHistoryFee") }}</span>
            <strong>{{ feeAmount }} {{ feeSymbol }}</strong>
          </div>
          <div class="physical-card-detail-info__row">
            <span>{{ t("card.cardNumber") }}：</span>
            <strong>{{ cardNumber }}</strong>
          </div>
          <div class="physical-card-detail-info__row">
            <span>{{ t("card.openCardHistoryApplicationStatus") }}</span>
            <strong class="is-approved">
              {{ t("card.openCardHistoryPhysicalActivationStatus") }}
            </strong>
          </div>
          <div class="physical-card-detail-info__row">
            <span>{{ t("card.openCardHistoryApprovedAt") }}</span>
            <strong>{{ finishedAt }}</strong>
          </div>
        </div>
      </section>

      <section class="physical-card-detail-active-notice">
        <app-image :src="physicalActiveImage" fit="contain" alt="" />
        <div>
          <strong>{{
            t("card.openCardHistoryPhysicalStatusNoticeTitle")
          }}</strong>
          <span>{{ t("card.openCardHistoryPhysicalStatusNoticeDesc") }}</span>
        </div>
        <button type="button" @click="goSetAtmPassword">
          {{ t("card.openCardHistoryPhysicalSetAtmAction") }}
        </button>
      </section>
    </template>

    <template v-else-if="status === 'pending'">
      <section
        class="physical-card-detail-hero physical-card-detail-hero--pending"
      >
        <app-image
          class="physical-card-detail-pending-image"
          :src="physicalPendingImage"
          alt=""
        />
        <h1>{{ t("card.openCardHistoryPhysicalPendingTitle") }}</h1>
        <span class="physical-card-detail-status is-pending">
          {{ t("card.openCardHistoryPhysicalPendingStatus") }}
        </span>
        <p>{{ t("card.openCardHistoryPhysicalPendingDesc") }}</p>
      </section>

      <section class="physical-card-detail-pending-notice">
        <app-image
          :src="physicalPendingNoticeImage"
          width="42"
          height="42"
          fit="contain"
          alt=""
        />
        <div>
          <strong>{{
            t("card.openCardHistoryPhysicalPendingNoticeTitle")
          }}</strong>
          <span>{{ t("card.openCardHistoryPhysicalPendingNoticeDesc") }}</span>
        </div>
      </section>

      <button
        type="button"
        class="physical-card-detail-support"
        @click="router.push('/mine/support-chat')"
      >
        <app-image
          :src="supportIcon"
          width="20"
          height="20"
          fit="contain"
          alt=""
        />
        <span>{{ t("card.openCardHistoryContactSupport") }}</span>
      </button>
    </template>

    <template v-else>
      <section
        class="physical-card-detail-hero physical-card-detail-hero--rejected"
      >
        <div class="physical-card-detail-rejected-image">
          <app-image :src="rejectedImage" alt="" />
        </div>
        <h1>{{ t("card.openCardHistoryPhysicalRejectedTitle") }}</h1>
        <span class="physical-card-detail-status is-rejected">
          {{ t("card.openCardHistoryPhysicalRejectedStatus") }}
        </span>
        <p class="physical-card-detail-failure-reason">
          {{ t("card.openCardHistoryFailureReason") }}{{ failureReason }}
        </p>
        <p class="physical-card-detail-failure-desc">
          {{ t("card.openCardHistoryFailureDesc", { name: cardName }) }}
        </p>
      </section>
    </template>
  </main>
</template>

<script setup>
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { navigateWithState } from "@/utils/route-state";
import { formatCardTimestamp } from "@/view/card/utils";
import physicalPendingImage from "@/assets/images/cardImage/card-open-history-physical-pending.png";
import physicalPendingNoticeImage from "@/assets/images/cardImage/pending-img.png";
import physicalSuccessImage from "@/assets/images/cardImage/card-open-history-physical-success.png";
import physicalActiveImage from "@/assets/images/cardImage/card-open-history-physical-active.png";
import rejectedImage from "@/assets/images/cardImage/card-open-history-failed.png";
import supportIcon from "@/assets/images/SVG/icon/card-open-history-support.svg";

const props = defineProps({
  record: {
    type: Object,
    default: () => ({}),
  },
  status: {
    type: String,
    default: "rejected",
  },
});

const { t } = useI18n();
const router = useRouter();
const record = computed(() => props.record || {});
const status = computed(() => props.status);

function getTime(value) {
  if (!value) return "--";
  const numericValue = Number(value);
  return Number.isFinite(numericValue)
    ? formatCardTimestamp(numericValue)
    : String(value);
}

const cardName = computed(() =>
  String(
    record.value?.name ||
      record.value?.cardName ||
      record.value?.templateName ||
      "Lotus Card",
  ).trim(),
);
const feeAmount = computed(() =>
  String(
    record.value?.openAmount ??
      record.value?.amount ??
      record.value?.openChargeAmount ??
      "0",
  ),
);
const feeSymbol = computed(() =>
  String(record.value?.openSymbol || record.value?.symbol || "USDT").trim(),
);
const appliedAt = computed(() =>
  getTime(
    record.value?.createTime ||
      record.value?.createAt ||
      record.value?.applyTime ||
      record.value?.createdAt,
  ),
);
const finishedAt = computed(() =>
  getTime(
    record.value?.auditTime ||
      record.value?.reviewTime ||
      record.value?.finishTime ||
      record.value?.updateTime ||
      record.value?.updatedAt,
  ),
);
const cardNumber = computed(() => {
  const directValue =
    record.value?.maskedPan ||
    record.value?.cardNo ||
    record.value?.cardNum ||
    record.value?.cardNumber;
  if (directValue) return String(directValue);

  const bin = String(record.value?.bin || "****");
  const last4 = String(record.value?.last4 || "****");
  return `${bin}-********-${last4}`;
});
const failureReason = computed(() =>
  String(
    record.value?.reason ||
      record.value?.rejectReason ||
      record.value?.failReason ||
      t("card.openCardHistoryDefaultFailureReason"),
  ).trim(),
);

function goSetAtmPassword() {
  navigateWithState(router, "/card/atm", {
    namespace: "card-atm",
    payload: {
      cardId: record.value?.id || record.value?.cardId || "",
    },
  });
}
</script>

<style scoped lang="scss">
.physical-card-open-history-detail-page {
  min-height: var(--app-viewport-height);
  padding-bottom: calc(24px + env(safe-area-inset-bottom));
  background: #ffffff;
  position: relative;
}

.physical-card-detail-hero {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 22px 16px 0;
  text-align: center;

  h1 {
    margin: 14px 0 10px;
    color: #090a1d;
    font-size: 20px;
    font-weight: 600;
    line-height: 28px;
  }

  p {
    max-width: 235px;
    margin: 12px 0 0;
    color: #747b88;
    font-size: 13px;
    line-height: 20px;
  }
}

.physical-card-detail-hero--approved {
  padding-top: 18px;
}

.physical-card-detail-success-image {
  width: 150px;
  height: 107px;
  overflow: hidden;

  .app-image {
    width: 127.75%;
    height: 119.53%;
    max-width: none;
    object-fit: fill;
    transform: translate(-13.44%, -9.92%);
  }
}

.physical-card-detail-success-content {
  display: flex;
  width: 257px;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  margin-top: 20px;

  h1 {
    width: 263px;
    margin: 0;
    font-size: 20px;
    line-height: 20px;
    white-space: nowrap;
  }

  p {
    margin: 0;
  }
}

.physical-card-detail-pending-image {
  width: 100px;
  height: 100px;
  margin-top: 1px;
  object-fit: contain;
}

.physical-card-detail-status {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;

  &.is-approved {
    color: #07b077;
    background: rgba(52, 191, 105, 0.12);
  }

  &.is-pending {
    color: #2667ff;
    background: #eaf2ff;
  }

  &.is-rejected {
    color: #ef0027;
    background: #ffe8ec;
  }
}

.physical-card-detail-info {
  margin: 22px 16px 0;

  h2 {
    position: relative;
    margin: 0 0 20px;
    padding-left: 10px;
    color: #000000;
    font-size: 16px;
    font-weight: 600;
    line-height: 16px;

    &::before {
      position: absolute;
      top: 1px;
      left: 0;
      width: 4px;
      height: 14px;
      border-radius: 4px;
      background: #2667ff;
      content: "";
    }
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__row {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    min-height: 16px;
    color: #333333;
    font-size: 14px;
    line-height: 16px;

    span {
      flex: 0 0 auto;
    }

    strong {
      min-width: 0;
      color: #000000;
      font-weight: 600;
      text-align: right;
      word-break: break-word;
    }

    .is-approved {
      color: #07b077;
    }
  }
}

.physical-card-detail-active-notice,
.physical-card-detail-pending-notice {
  display: flex;
  align-items: center;
  margin: 28px 16px 0;
  padding: 16px;
  border-radius: 16px;
  background: #eaf2ff;

  > .app-image {
    flex: 0 0 42px;
    margin-right: 10px;
    object-fit: contain;
  }

  > div {
    min-width: 0;
    flex: 1;
    text-align: left;

    strong,
    span {
      display: block;
    }

    strong {
      color: #090a1d;
      font-size: 16px;
      font-weight: 500;
      line-height: 20px;
    }

    span {
      margin-top: 4px;
      color: #747b88;
      font-size: 12px;
      line-height: 16px;
    }
  }
}

.physical-card-detail-active-notice {
  min-height: 92px;

  button {
    flex: 0 0 60px;
    width: 60px;
    height: 28px;
    margin-left: 7px;
    border: 1px solid #2667ff;
    border-radius: 6px;
    background: #eaf2ff;
    color: #2667ff;
    font-size: 12px;
    line-height: 12px;
  }
}

.physical-card-detail-pending-notice {
  min-height: 90px;
  margin-top: 24px;
}

.physical-card-detail-support {
  display: flex;
  width: calc(100% - 32px);
  position: absolute;
  bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  min-height: 44px;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 0 16px 0;
  border: 0;
  border-radius: 999px;
  background: #d5e5ff;
  color: #2667ff;
  font-size: 16px;
  font-weight: 500;
  line-height: 22px;
}

.physical-card-detail-rejected-image {
  width: 138px;
  height: 123px;

  .app-image {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.physical-card-detail-failure-reason {
  color: #ef0027 !important;
}

.physical-card-detail-failure-desc {
  max-width: 332px !important;
  text-align: left;
}
</style>
